import { useParams, usePathname } from 'next/navigation';

import { useCallback } from 'react';

import { urlMap } from '@nocowanie/core/consts';

import {
    searchStateSlice,
    setFiltersVisibilityAction,
    toggleFiltersVisibilityAction,
    updateSearchCriteriaAction,
    updateSearchFiltersAction,
    useAppDispatch,
    useAppSelector,
} from '@app/app-state';
import { adjustOnlineBookingFilters } from '@app/helpers/filters.helpers';
import { mapSearchStateToUrlParams } from '@app/helpers/search-params.helpers';
import { useInitialRenderFlag } from '@app/hooks/useInitialRenderFlag';
import { useIsMobileRes } from '@app/hooks/useIsMobileRes';
import { SearchCriteria, SearchFilters } from '@app/models';

/**
 * Hook to access and update search state
 */
export function useSearch() {
    const dispatch = useAppDispatch();
    const currentPage = useAppSelector(searchStateSlice.selectors.selectPage);
    const searchCriteria = useAppSelector(searchStateSlice.selectors.selectSearchCriteria);
    const filters = useAppSelector(searchStateSlice.selectors.selectSearchFilters);
    const hasGeoChanged = useAppSelector(searchStateSlice.selectors.selectHasGeoChanged);
    const isSearchLoading = useAppSelector(searchStateSlice.selectors.selectIsSearchLoading);
    const params = useParams();
    const pathname = usePathname();
    const isInitialRender = useInitialRenderFlag();
    const isCampaignListing = pathname?.includes(urlMap.campaignListing);
    const campaignListingId =
        typeof params?.slug === 'string' && isCampaignListing ? params.slug : undefined;
    const isFiltersContentVisible = useAppSelector(
        searchStateSlice.selectors.selectIsFiltersContentVisible,
    );
    const isMobileRes = useIsMobileRes();

    const updateSearchCriteria = useCallback(
        (criteria: Partial<SearchCriteria>, shouldUpdateUrl = false) => {
            dispatch(
                updateSearchCriteriaAction({
                    criteria,
                    shouldUpdateUrl,
                }),
            );
        },
        [],
    );

    const updateSearchFilters = useCallback(
        (newFilters: Partial<SearchFilters>, shouldUpdateUrl = false) => {
            dispatch(
                updateSearchFiltersAction({
                    filters: adjustOnlineBookingFilters(filters, { ...filters, ...newFilters }),
                    shouldUpdateUrl,
                }),
            );
        },
        [filters],
    );

    const toggleFiltersVisibility = useCallback(() => {
        setTimeout(() => {
            dispatch(toggleFiltersVisibilityAction());
        }, 0);
    }, []);

    const setFiltersVisibility = useCallback((open: boolean) => {
        dispatch(setFiltersVisibilityAction({ open }));
    }, []);

    const getAsUrlParams = useCallback(() => {
        return mapSearchStateToUrlParams(searchCriteria || {}, filters || {});
    }, [searchCriteria, filters]);

    return {
        searchCriteria,
        filters,
        isSearchLoading,
        isInitialRender,
        isFiltersContentVisible,
        isMobileRes,
        updateSearchCriteria,
        updateSearchFilters,
        toggleFiltersVisibility,
        setFiltersVisibility,
        campaignListingId,
        getAsUrlParams,
        hasGeoChanged,
        currentPage,
    };
}
